/**
 * Compliance Certificate Viewer
 *
 * Demo Step 5: Navigate to compliance dashboard. Generate a SOC 2 compliance
 * certificate with hash chain verification.
 *
 * Shows:
 *   - Certificate metadata (type, run ID, issued time)
 *   - Enforcement summary (evaluations, blocks, warnings, scans, tokens)
 *   - Hash chain integrity verification with visual chain links
 *   - Control mapping with evidence counts
 *   - Certificate hash (self-signed)
 */

import React, { useState } from 'react';

const CONTROL_STATUS_COLORS = {
  PASSED:         { bg: '#dcfce7', text: '#166534', icon: '✓' },
  FAILED:         { bg: '#fef2f2', text: '#991b1b', icon: '✕' },
  NOT_APPLICABLE: { bg: '#f3f4f6', text: '#6b7280', icon: '—' },
};

export function ComplianceCertificateViewer({ certificate, onClose, onGenerate }) {
  const [expandedControl, setExpandedControl] = useState(null);
  const [showRawJson, setShowRawJson] = useState(false);

  if (!certificate) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 8,
        border: '1px solid #e5e7eb',
        padding: 24,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📜</div>
        <div style={{ fontWeight: 600, color: '#374151', fontSize: 14, marginBottom: 8 }}>
          No Certificate Generated Yet
        </div>
        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 16 }}>
          Execute a protected workflow to generate a compliance certificate
          with hash chain verification.
        </div>
        {onGenerate && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={() => onGenerate('SOC2_TYPE_II')} style={btnStyle('#1e40af')}>
              Generate SOC 2 Certificate
            </button>
            <button onClick={() => onGenerate('HIPAA')} style={btnStyle('#059669')}>
              Generate HIPAA Certificate
            </button>
          </div>
        )}
      </div>
    );
  }

  const { enforcementSummary, hashChain, controlMapping } = certificate;

  return (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      fontSize: 13,
    }}>
      {/* Certificate Header */}
      <div style={{
        background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
        color: '#fff',
        padding: '14px 18px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 20 }}>📜</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {certificate.certificateType === 'SOC2_TYPE_II' ? 'SOC 2 Type II' : certificate.certificateType}
              {' '}Compliance Certificate
            </div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>
              Cryptographically verified protection proof
            </div>
          </div>
          {hashChain?.integrityVerified && (
            <span style={{
              marginLeft: 'auto',
              background: '#10b981',
              padding: '4px 10px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 700,
            }}>
              ✓ INTEGRITY VERIFIED
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: 11, opacity: 0.8 }}>
          <span>ID: {certificate.certificateId}</span>
          <span>Issued: {new Date(certificate.issuedAt).toLocaleString()}</span>
          <span>Run: {certificate.workflowRunId?.substring(0, 8)}...</span>
        </div>
      </div>

      {/* Enforcement Summary */}
      {enforcementSummary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          borderBottom: '1px solid #e5e7eb',
        }}>
          {[
            { label: 'Policy Evaluations', value: enforcementSummary.totalPolicyEvaluations, color: '#1e40af' },
            { label: 'DPG Scans', value: enforcementSummary.dpgScansPerformed, color: '#0d9488' },
            { label: 'Tokens Issued', value: enforcementSummary.tokensIssued, color: '#059669' },
            { label: 'Compliance Rate', value: enforcementSummary.complianceRate, color: '#dc2626' },
          ].map((stat, i) => (
            <div key={i} style={{
              padding: '10px 14px',
              textAlign: 'center',
              borderRight: i < 3 ? '1px solid #e5e7eb' : 'none',
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Hash Chain Verification */}
      {hashChain && (
        <div style={{
          padding: '12px 18px',
          borderBottom: '1px solid #e5e7eb',
          background: hashChain.integrityVerified ? '#f0fdf4' : '#fef2f2',
        }}>
          <div style={{ fontWeight: 600, color: '#374151', fontSize: 12, marginBottom: 8 }}>
            HASH CHAIN VERIFICATION
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontFamily: 'monospace',
            fontSize: 11,
          }}>
            {/* Genesis */}
            <div style={{
              background: '#e5e7eb',
              padding: '4px 8px',
              borderRadius: 4,
              color: '#374151',
            }}>
              Genesis: {hashChain.genesisHash?.substring(0, 8)}...
            </div>

            {/* Chain Links */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {Array.from({ length: Math.min(hashChain.chainLength, 12) }).map((_, i) => (
                <div key={i} style={{
                  width: 6, height: 6,
                  borderRadius: '50%',
                  background: hashChain.integrityVerified ? '#16a34a' :
                    (i === hashChain.brokenAtIndex ? '#dc2626' : '#16a34a'),
                }} />
              ))}
              {hashChain.chainLength > 12 && (
                <span style={{ fontSize: 10, color: '#6b7280' }}>+{hashChain.chainLength - 12}</span>
              )}
            </div>

            {/* Arrow */}
            <span style={{ color: '#9ca3af' }}>→</span>

            {/* Final Hash */}
            <div style={{
              background: hashChain.integrityVerified ? '#dcfce7' : '#fef2f2',
              border: `1px solid ${hashChain.integrityVerified ? '#86efac' : '#fecaca'}`,
              padding: '4px 8px',
              borderRadius: 4,
              color: hashChain.integrityVerified ? '#166534' : '#991b1b',
            }}>
              Final: {hashChain.finalHash?.substring(0, 12)}...
            </div>

            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6b7280' }}>
              {hashChain.algorithm} | {hashChain.chainLength} events
            </span>
          </div>
        </div>
      )}

      {/* Control Mapping */}
      {controlMapping && (
        <div style={{ padding: '12px 18px' }}>
          <div style={{ fontWeight: 600, color: '#374151', fontSize: 12, marginBottom: 8 }}>
            CONTROL MAPPING
          </div>
          {controlMapping.map((control, i) => {
            const status = CONTROL_STATUS_COLORS[control.status] || CONTROL_STATUS_COLORS.NOT_APPLICABLE;
            const isExpanded = expandedControl === i;

            return (
              <div key={i} style={{
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                marginBottom: 6,
                overflow: 'hidden',
              }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    cursor: control.evidence ? 'pointer' : 'default',
                    background: isExpanded ? '#f9fafb' : '#fff',
                  }}
                  onClick={() => control.evidence && setExpandedControl(isExpanded ? null : i)}
                >
                  <span style={{
                    background: status.bg,
                    color: status.text,
                    width: 22, height: 22,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {status.icon}
                  </span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, color: '#374151', fontSize: 12 }}>
                      {control.controlId}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
                      {control.controlName}
                    </span>
                  </div>
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>
                    {control.evidenceCount > 0 ? `${control.evidenceCount} evidence` : ''}
                  </span>
                </div>

                {isExpanded && control.evidence && (
                  <div style={{ padding: '0 12px 8px', borderTop: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 11, color: '#6b7280', padding: '6px 0' }}>
                      {control.controlDescription}
                    </div>
                    {control.evidence.map((ev, j) => (
                      <div key={j} style={{
                        display: 'flex',
                        gap: 8,
                        padding: '4px 0',
                        borderBottom: j < control.evidence.length - 1 ? '1px dashed #f3f4f6' : 'none',
                        fontFamily: 'monospace',
                        fontSize: 10,
                        color: '#374151',
                      }}>
                        <span style={{ color: '#9ca3af' }}>{new Date(ev.timestamp).toLocaleTimeString()}</span>
                        <span style={{
                          background: ev.decision === 'ALLOWED' ? '#dcfce7' : ev.decision === 'BLOCKED' ? '#fef2f2' : '#fef3c7',
                          padding: '0 4px',
                          borderRadius: 2,
                          fontSize: 9,
                        }}>
                          {ev.decision}
                        </span>
                        <span>{ev.eventType}</span>
                        <span style={{ color: '#9ca3af' }}>#{ev.eventHash}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Certificate Self-Hash */}
      {certificate.certificateHash && (
        <div style={{
          padding: '10px 18px',
          borderTop: '1px solid #e5e7eb',
          background: '#f9fafb',
          fontFamily: 'monospace',
          fontSize: 10,
          color: '#6b7280',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ fontWeight: 600 }}>Certificate Hash (SHA-256):</span>
          <code style={{ wordBreak: 'break-all' }}>{certificate.certificateHash}</code>
          <button
            onClick={() => setShowRawJson(!showRawJson)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: '1px solid #d1d5db',
              borderRadius: 3,
              padding: '2px 8px',
              fontSize: 10,
              cursor: 'pointer',
              color: '#6b7280',
            }}
          >
            {showRawJson ? 'Hide' : 'Show'} Raw JSON
          </button>
        </div>
      )}

      {showRawJson && (
        <div style={{
          padding: '8px 18px',
          background: '#292524',
          maxHeight: 200,
          overflowY: 'auto',
        }}>
          <pre style={{
            fontFamily: 'monospace',
            fontSize: 10,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}>
            {JSON.stringify(certificate, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function btnStyle(bg) {
  return {
    padding: '8px 16px',
    background: bg,
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  };
}

export default ComplianceCertificateViewer;
